import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { z } from "zod";
import { useAuth } from "@/auth/auth-provider";
import { PageHeader } from "@/components/page-header";
import { ReportScopeFilters } from "@/components/report-scope-filters";
import { ReportingFeederPicker, ReportingPeriodPicker } from "@/components/reporting-controls";
import {
  completedWatPeriod,
  STANDARD_READING_COLUMNS, workbookFeeders, workbookScope, workbookView,
} from "@/lib/reporting-compose";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/components/ui/toast";
import {
  api,
  ApiError,
  reportingApi,
  type AnalyticsFeeder,
  type ReportingCapabilities,
  type ReportingRun,
  type ReportingSubscription,
  type ReportingSubscriptionInput,
} from "@/lib/api";
import {
  parseRecipients,
  reportingAdmin,
  reportingDeliveryStatus,
  reportingError,
  reportingRangeError,
  reportingSchema,
  reportingTime,
  subscriptionPeriod,
  reportingPeriodDescription,
} from "@/lib/reporting";
import { REPORT_DRAFT_PARAM, readReportDraft, reportScopeMatches, safeRunReportUrl, validReportView, type ReportDraft } from "@/lib/report-links";

const panel = "space-y-4 rounded-xl border bg-card p-4 shadow-sm sm:p-6";
const selectClass = "block w-full rounded-md border border-input bg-background p-2 text-sm";

export function ReportingPage({ workspace }: { workspace: "exports" | "schedules" }): JSX.Element {
  const { me } = useAuth();
  const [search, setSearch] = useSearchParams();
  const previousOrganization = useRef(me?.tenant_id);
  useEffect(() => {
    if (previousOrganization.current !== me?.tenant_id) {
      previousOrganization.current = me?.tenant_id;
      const next = new URLSearchParams(search);
      next.delete(REPORT_DRAFT_PARAM);
      setSearch(next, { replace: true });
    }
  }, [me?.tenant_id, search, setSearch]);
  if (workspace === "schedules" && !reportingAdmin(me)) {
    return <Navigate to="/reporting/exports" replace />;
  }
  // Remount all draft state on organization changes; never carry recipients/scope across tenants.
  return (
    <ReportingWorkspace
      key={`${me?.tenant_id ?? "none"}:${workspace}`}
      workspace={workspace}
      organizationId={me?.tenant_id ?? null}
      selfEmail={me?.email ?? null}
      admin={reportingAdmin(me)}
    />
  );
}

function ReportingWorkspace({
  workspace,
  organizationId,
  selfEmail,
  admin,
}: {
  workspace: "exports" | "schedules";
  organizationId: number | null;
  selfEmail: string | null;
  admin: boolean;
}): JSX.Element {
  const client = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState<ReportingSubscription | null>(null);
  const [revision, setRevision] = useState(0);
  const [creatingSchedule, setCreatingSchedule] = useState(false);
  const [testTarget, setTestTarget] = useState<ReportingSubscription | null>(null);
  const [testFeedback, setTestFeedback] = useState("");
  const [saveFeedback, setSaveFeedback] = useState("");
  const [scheduleSearch, setScheduleSearch] = useState("");
  const [historyProduct, setHistoryProduct] = useState("all");
  const [historyStatus, setHistoryStatus] = useState("all");
  const testInFlight = useRef(false);
  const [search, setSearch] = useSearchParams();
  const [rejectedIntent] = useState(() => {
    try {
      return search.has(REPORT_DRAFT_PARAM) &&
        JSON.parse(search.get(REPORT_DRAFT_PARAM)!).organization_id !== organizationId;
    } catch { return search.has(REPORT_DRAFT_PARAM); }
  });
  const capabilities = useQuery({
    queryKey: ["reporting", organizationId, "capabilities"],
    queryFn: ({ signal }) => reportingApi.capabilities(signal),
    enabled: !!organizationId,
  });
  const active = !!organizationId && capabilities.data?.enabled === true;
  const feeders = useQuery({
    queryKey: ["reporting", organizationId, "feeders"],
    queryFn: ({ signal }) => api.analyticsFeeders(signal, organizationId ?? undefined),
    enabled: active,
  });
  const runs = useQuery({
    queryKey: ["reporting", organizationId, "runs"],
    queryFn: ({ signal }) => reportingApi.runs(signal),
    enabled: active && workspace === "exports",
    refetchInterval: active && workspace === "exports" ? 30000 : false,
  });
  const subscriptions = useQuery({
    queryKey: ["reporting", organizationId, "subscriptions"],
    queryFn: ({ signal }) => reportingApi.subscriptions(signal),
    enabled: active && admin && workspace === "schedules",
  });
  const draft = !rejectedIntent && feeders.data ? readReportDraft(search, organizationId, feeders.data) : null;
  const discardDraft = () => {
    const next = new URLSearchParams(search);
    next.delete(REPORT_DRAFT_PARAM);
    setSearch(next, { replace: true });
  };
  const refresh = () => client.invalidateQueries({ queryKey: ["reporting", organizationId] });
  const failure = (error: Error) =>
    toast({
      title: "Reporting request failed",
      description: reportingError(error.message),
      variant: "destructive",
    });
  const sendTest = useMutation({
    retry: false,
    mutationFn: (id: string) => reportingApi.testSubscription(id),
    onSuccess: (run) => {
      setTestFeedback(`Test queued · ${run.id}. Only your account mailbox is contacted. Inspect run history for progress; queueing does not confirm inbox delivery.`);
      setTestTarget(null);
      void refresh();
    },
    onError: (error: Error) => {
      setTestTarget(null);
      setTestFeedback(error instanceof ApiError && error.status < 500
        ? `Test request failed: ${reportingError(error.message)}`
        : "Test request outcome is unknown. Inspect and refresh run history before considering another request; do not resubmit because the test may already be queued.");
      void refresh();
    },
    onSettled: () => { testInFlight.current = false; },
  });
  const pause = useMutation({
    retry: false,
    mutationFn: (subscription: ReportingSubscription) => {
      const {
        name,
        template,
        source,
        feeder_ids,
        columns,
        timezone,
        recipients,
        schedule,
        enabled,
      } = subscription;
      return reportingApi.saveSubscription(
        {
          name,
          template,
          source,
          feeder_ids,
          columns,
          timezone,
          recipients,
          schedule,
          period: subscriptionPeriod(subscription),
          publication_delay_minutes: subscription.publication_delay_minutes ?? 0,
          ...(subscription.report_view !== undefined ? { report_view: subscription.report_view } : {}),
          enabled: !enabled,
        },
        subscription.id,
      );
    },
    onSuccess: () => {
      void refresh();
      toast({ title: "Subscription updated", variant: "success" });
    },
    onError: failure,
  });
  const download = useMutation({
    mutationFn: async (run: ReportingRun) => {
      const blob = await reportingApi.download(run.id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = run.format === "xlsx" ? `report-${run.id}.xlsx` : `telemetry-${run.id}.csv`;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    },
    onError: failure,
  });
  const error = capabilities.error ?? feeders.error ??
    (workspace === "exports" ? runs.error : subscriptions.error);
  return (
    <div className="space-y-6">
      <PageHeader
        title={workspace === "exports" ? "Report exports" : "Scheduled deliveries"}
        description={workspace === "exports"
          ? "Create a report for your organization, then download or email the issued file."
          : "Manage recurring reports, review their next delivery, and send a safe test to yourself."}
      />
      <details className="text-sm text-muted-foreground">
        <summary className="cursor-pointer">About report formats</summary>
        <p className="mt-2 max-w-3xl">
        Underlying readings are not exact chart-data exports: no chart aggregation, transformations,
        or downsampling are applied. Executive workbooks contain calculated report tables.
        Image and PDF output are deferred.
        </p>
      </details>
      {search.has(REPORT_DRAFT_PARAM) && (rejectedIntent || feeders.isSuccess && !draft) ? (
        <div role="alert">Report draft rejected: invalid, changed, oversized, or different organization scope.
          Nothing was saved or queued. <Button variant="outline" onClick={discardDraft}>Discard report draft</Button>
        </div>
      ) : null}
      {!organizationId ? (
        <p role="alert">
          Select a concrete current organization before viewing or creating exports. All-platform
          exports are not supported.
        </p>
      ) : null}
      {error ? (
        <div role="alert" className="space-y-2 text-destructive">
          <p>{reportingError(error.message)}</p>
          <Button variant="outline" onClick={() => void refresh()}>Reload reporting data</Button>
        </div>
      ) : null}
      {capabilities.isLoading ? <p>Loading reporting capabilities…</p> : null}
      {capabilities.data && !capabilities.data.enabled ? (
        <p role="status">
          Reporting is disabled for this deployment. No exports or schedules can be queued.
        </p>
      ) : null}
      {active && capabilities.data ? (
        <>
          {capabilities.data.test_delivery_only ? (
            <p role="status" className="rounded-lg border-2 border-amber-500 bg-amber-500/10 p-4 font-medium">
              Test-only email delivery: recipients are restricted to approved test addresses.
              The server rejects unapproved addresses. Download-only CSV exports remain available.
            </p>
          ) : null}
          {saveFeedback ? <p role="status" className="rounded-lg border bg-muted/30 p-4">{saveFeedback}</p> : null}
          <details className="rounded-lg border bg-muted/30 p-4 text-sm" aria-label="Export policy">
            <summary className="cursor-pointer font-medium">
              Limits, data coverage & delivery policy
            </summary>
            <div className="mt-3 space-y-3 text-muted-foreground">
              <p>
                Limits per export: {capabilities.data.limits.max_rows.toLocaleString()} rows ·{" "}
                {capabilities.data.limits.max_bytes.toLocaleString()} bytes ·{" "}
                {capabilities.data.limits.max_range_days} days. Narrow your selection if a limit is
                exceeded; partial exports are not complete results.
              </p>
              <p>
                Requests are queued. Dispatch is periodic (planned five-minute cadence), not
                immediate; scheduled times are not exact delivery guarantees.
              </p>
              <p>
                All mail is sent by Anietronic
                {capabilities.data.sender ? ` (${capabilities.data.sender})` : ""}. Sender identity
                is platform-controlled and cannot be overridden.
              </p>
              {!capabilities.data.email_enabled ? (
                <p role="status">
                  Email delivery is unavailable: platform mail configuration is not enabled. Exports
                  without recipients remain available; active delivery schedules cannot be created
                  or resumed.
                </p>
              ) : null}
            </div>
          </details>
          {workspace === "exports" ? (
            <section aria-label="Report exports" className="space-y-6">
              {feeders.isLoading ? <p>Loading authorized feeders…</p> : null}
              {feeders.isSuccess && !feeders.data.length ? (
                <p role="status">
                  No authorized feeders are available in this organization. Export creation is
                  unavailable.
                </p>
              ) : null}
              {feeders.data?.length && (!search.has(REPORT_DRAFT_PARAM) || draft) ? (
                <ExportForm
                  key={`export:${revision}:${search.get(REPORT_DRAFT_PARAM) ?? ""}`}
                  draft={draft}
                  capabilities={capabilities.data}
                  feeders={feeders.data}
                  admin={admin}
                  editing={null}
                  mode="run"
                  onSaved={() => {
                    setSaveFeedback("Export queued. Track file generation and email attempts separately in Run history below. Queueing does not confirm email delivery.");
                    discardDraft();
                    setEditing(null);
                    setRevision((v) => v + 1);
                    void refresh();
                  }}
                  onCancel={() => setEditing(null)}
                />
              ) : null}
            </section>
          ) : null}
          {workspace === "schedules" && admin ? (
            <section aria-label="Scheduled deliveries" className="space-y-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold">Delivery schedules</h2>
                  <p className="text-sm text-muted-foreground">
                    Recurring report files for approved recipients.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => void subscriptions.refetch()}>
                    Refresh subscriptions
                  </Button>
                  <Button
                    onClick={() => {
                      discardDraft();
                      setEditing(null);
                      setRevision((value) => value + 1);
                      setCreatingSchedule(true);
                    }}
                  >
                    New subscription
                  </Button>
                </div>
              </div>
              {(creatingSchedule || editing || draft) && feeders.data?.length &&
                (!search.has(REPORT_DRAFT_PARAM) || draft) ? (
                <ExportForm
                  key={`${editing?.id ?? "schedule"}:${revision}:${search.get(REPORT_DRAFT_PARAM) ?? ""}`}
                  draft={editing ? null : draft}
                  capabilities={capabilities.data}
                  feeders={feeders.data}
                  admin={admin}
                  editing={editing}
                  mode="subscription"
                  onSaved={() => {
                    setSaveFeedback("Subscription saved. Check its Active or Paused status and next run below. Saving does not immediately generate an export or send an email.");
                    discardDraft();
                    setEditing(null);
                    setCreatingSchedule(false);
                    setRevision((v) => v + 1);
                    void refresh();
                  }}
                  onCancel={() => {
                    discardDraft();
                    setEditing(null);
                    setCreatingSchedule(false);
                  }}
                />
              ) : null}
              <section className={panel} aria-label="Subscriptions">
                <h2 className="text-lg font-semibold">Saved deliveries</h2>
                <Input aria-label="Search saved deliveries" placeholder="Find a delivery by name or recipient…"
                  value={scheduleSearch} onChange={(event) => setScheduleSearch(event.target.value)} />
                <details className="text-sm text-muted-foreground">
                <summary className="cursor-pointer">How self-only tests work</summary>
                <p className="mt-2">
                  Send test uses the saved definition and its last completed local period, even when paused.
                  Saved recipients are NOT contacted. The server sends only to the current administrator’s own account mailbox.
                  Draft edits, saved recipients and the enabled flag are not changed.
                </p>
                </details>
                {!capabilities.data.email_enabled ? <p role="status">Send test unavailable: email delivery is disabled.</p> : null}
                {testFeedback ? <p role="status">{testFeedback}{" "}
                  <Link className="underline" to="/reporting/exports#run-history">View run history</Link>
                </p> : null}
                {subscriptions.isLoading ? <p>Loading subscriptions…</p> : null}
                {subscriptions.data?.length === 0 ? <p>No subscriptions yet.</p> : null}
                {subscriptions.data?.filter((subscription) => [subscription.name, ...subscription.recipients]
                  .join(" ").toLocaleLowerCase().includes(scheduleSearch.trim().toLocaleLowerCase())).map((subscription) => (
                  <article key={subscription.id} className="space-y-3 rounded-lg border p-4 text-sm">
                    <h3 className="flex flex-wrap items-center gap-3 font-semibold">
                      {subscription.name}
                      <span className="rounded-full bg-muted px-2 py-1 text-xs font-normal">{subscription.enabled ? "Active" : "Paused"}</span>
                    </h3>
                    <p className="text-xs text-muted-foreground">{subscription.template === "executive-report" ? "Executive workbook" : "Supporting readings CSV"} · {subscription.feeder_ids.length} feeders</p>
                    <div className="grid gap-3 md:grid-cols-3">
                    <div><p className="mb-1 text-xs text-muted-foreground">Schedule</p>
                    <p>
                      {subscription.schedule.frequency} at {subscription.schedule.time} (
                      {subscription.timezone})
                      {subscription.schedule.frequency === "weekly"
                        ? ` · ${["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"][subscription.schedule.weekday ?? 0]}`
                        : ""}
                    </p>
                    </div>
                    <div><p className="mb-1 text-xs text-muted-foreground">Report period</p>
                    <p>{reportingPeriodDescription(subscriptionPeriod(subscription))}</p>
                    {subscription.publication_delay_minutes ? <p className="text-xs text-muted-foreground">Delivery delay: {subscription.publication_delay_minutes} minutes</p> : null}
                    {subscription.schedule.frequency === "monthly" ? <p>Day {subscription.schedule.month_day} of month (clamped to last day in shorter months)</p> : null}
                    </div>
                    <div><p className="mb-1 text-xs text-muted-foreground">Recipients</p>
                    <p className="break-words">{subscription.recipients.join(", ") || "None"}</p>
                    </div>
                    </div>
                    <p>
                      Next run:{" "}
                      {subscription.next_run_at
                        ? reportingTime(subscription.next_run_at, subscription.timezone)
                        : "Not scheduled"}
                    </p>
                    <div className="flex flex-wrap gap-2 border-t pt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        aria-label={`Send test ${subscription.name}`}
                        disabled={sendTest.isPending || !organizationId || !capabilities.data.email_enabled}
                        onClick={() => setTestTarget(subscription)}
                      >
                        Send test to myself
                      </Button>
                      <Button variant="outline" size="sm" aria-label={`Edit ${subscription.name}`} onClick={() => setEditing(subscription)}>
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        aria-label={`${subscription.enabled ? "Pause" : "Resume"} ${subscription.name}`}
                        disabled={
                          pause.isPending ||
                          (!subscription.enabled && !capabilities.data?.email_enabled)
                        }
                        onClick={() => pause.mutate(subscription)}
                      >
                        {subscription.enabled ? "Pause" : "Resume"}
                      </Button>
                    </div>
                  </article>
                ))}
              </section>
              <Dialog open={testTarget !== null} onOpenChange={(open) => { if (!open && !sendTest.isPending) setTestTarget(null); }}>
                <DialogContent>
                  <DialogTitle>Send test of {testTarget?.name}?</DialogTitle>
                  <DialogDescription>
                    Saved recipients are NOT contacted. The server sends only to your own account mailbox
                    {selfEmail ? `: ${selfEmail}` : " (resolved by the server; no fallback recipient)"}.
                    This uses the {testTarget ? reportingPeriodDescription(subscriptionPeriod(testTarget)) : "last completed local period"} in {testTarget?.timezone} from the SAVED definition,
                    not unsaved edits. It does not change the saved recipients, draft or enabled flag.
                    Publication delay is ignored for this self-only test.
                  </DialogDescription>
                  <DialogFooter>
                    <Button variant="outline" disabled={sendTest.isPending} onClick={() => setTestTarget(null)}>Cancel</Button>
                    <Button disabled={sendTest.isPending || !active || !admin || !capabilities.data.email_enabled}
                      onClick={() => {
                        if (!testTarget || testInFlight.current) return;
                        testInFlight.current = true;
                        setTestFeedback("");
                        sendTest.mutate(testTarget.id);
                      }}>
                      {sendTest.isPending ? "Queueing test…" : "Confirm send test"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <p className="text-sm text-muted-foreground">
                This page manages recurring delivery definitions. To generate a one-off report,
                download issued files or inspect delivery attempts,{" "}
                <Link className="underline" to="/reporting/exports#run-history">open Report exports</Link>.
              </p>
            </section>
          ) : null}
          {!admin ? (
            <p className="text-sm text-muted-foreground">
              Organization administrators manage email recipients and subscriptions. You can queue
              and download exports without email delivery.
            </p>
          ) : null}
          {workspace === "exports" ? <section id="run-history" className={panel} aria-label="Run history">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Run history</h2>
              <Button variant="outline" onClick={() => void runs.refetch()}>
                Refresh history
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Updates every 30 seconds. Provider acceptance is not confirmation of inbox delivery.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="space-y-1 text-xs text-muted-foreground">Filter history by report
                <select className={selectClass} value={historyProduct} onChange={(event) => setHistoryProduct(event.target.value)}>
                  <option value="all">All report types</option>
                  <option value="executive-report">Executive workbooks</option>
                  <option value="telemetry-readings">Supporting readings CSV</option>
                </select>
              </label>
              <label className="space-y-1 text-xs text-muted-foreground">Filter history by status
                <select className={selectClass} value={historyStatus} onChange={(event) => setHistoryStatus(event.target.value)}>
                  <option value="all">All statuses</option>
                  <option value="generated">Ready to download</option>
                  <option value="pending">Queued</option>
                  <option value="running">Generating</option>
                  <option value="failed">Failed</option>
                </select>
              </label>
            </div>
            {runs.isLoading ? <p>Loading runs…</p> : null}
            {runs.data?.length === 0 ? <p>No export runs yet.</p> : null}
            {runs.data?.filter((run) => (historyProduct === "all" || run.template === historyProduct) &&
              (historyStatus === "all" || run.status === historyStatus)).map((run) => (
              <article key={run.id} className="space-y-3 break-words rounded-lg border p-4 text-sm">
                <h3 className="flex flex-wrap items-center gap-3 font-medium">
                  <span>{run.format === "xlsx" ? "Executive workbook" : "Supporting readings CSV"}</span>
                  <span className="rounded-full border px-3 py-1">{run.is_test ? "Test · self-only" : "Export"}</span>
                  <span className="rounded-full bg-muted px-3 py-1 capitalize">
                    {run.status === "generated" ? `${run.format === "xlsx" ? "Workbook" : "CSV"} generated — email status is separate` : run.status}
                  </span>
                  <span>{reportingTime(run.created_at, run.timezone)}</span>
                </h3>
                {run.report_view ? (
                  <div>
                    <p>Report context: {run.report_view.report} · display timezone Africa/Lagos.</p>
                    {safeRunReportUrl(run, organizationId, window.location.origin, feeders.data ?? []) ? (
                      <a className="underline" href={safeRunReportUrl(run, organizationId, window.location.origin, feeders.data ?? [])!}>Open authenticated report</a>
                    ) : <p>Report link unavailable: {run.report_link_unavailable_reason ?? "No valid current app link."}</p>}
                    <p>Requeries current retained data (30 days), which may differ from the issued file. Links do not grant access.</p>
                  </div>
                ) : null}
                <details>
                  <summary className="cursor-pointer text-muted-foreground">
                    Export details · {run.id}
                  </summary>
                  <div className="mt-3 space-y-2 text-muted-foreground">
                    {run.is_test ? <p>Self-only test of a saved subscription · saved recipients were not contacted.</p> : null}
                    {run.is_test && run.source_test_subscription_id ? <p>Source saved subscription: {run.source_test_subscription_id}</p> : null}
                    {run.is_test && run.source_test_subscription_revision != null ? <p>Saved definition revision: {run.source_test_subscription_revision}</p> : null}
                    <p>
                      Source:{" "}
                      {run.source === "archive"
                        ? "Archived Parquet readings (archive freshness/coverage may differ from recent data)"
                        : "Retained PostgreSQL readings"}
                    </p>
                    <p>
                      From (inclusive): {reportingTime(run.from_time, run.timezone)}
                      <br />
                      {run.format === "xlsx" ? "To (dashboard query bound)" : "To (exclusive)"}: {reportingTime(run.to_time, run.timezone)}
                    </p>
                    <p>
                      Created: {reportingTime(run.created_at, run.timezone)} · Rows:{" "}
                      {run.row_count ?? "Not available"} · Bytes:{" "}
                      {run.byte_count ?? "Not available"}
                    </p>
                    {admin && run.recipients.length ? (
                      <p>Recipients: {run.recipients.join(", ")}</p>
                    ) : null}
                    {run.artifact_expires_at ? (
                      <p>{run.format === "xlsx" ? "Workbook" : "CSV"} expires: {reportingTime(run.artifact_expires_at, run.timezone)}</p>
                    ) : null}
                    {run.artifact_sha256 ? (
                      <p>{run.format === "xlsx" ? "Workbook" : "CSV"} SHA-256: <code className="break-all">{run.artifact_sha256}</code></p>
                    ) : null}
                    {run.data_cutoff ? (
                      <p>Data cutoff: {reportingTime(run.data_cutoff, run.timezone)}</p>
                    ) : null}
                    {run.source_snapshot?.resource_sha256 ? (
                      <p>Dashboard source SHA-256: <code className="break-all">{run.source_snapshot.resource_sha256}</code>
                        {run.source_snapshot.queried_at ? <> · Data queried: {reportingTime(run.source_snapshot.queried_at, run.timezone)}</> : null}
                      </p>
                    ) : null}
                    {run.source === "archive" && run.source_snapshot ? (
                      <p>
                        Archive publication revision: {run.source_snapshot.revision ?? "Unknown"}
                        {run.source_snapshot.publication_asof ? (
                          <> · As of: {reportingTime(run.source_snapshot.publication_asof, run.timezone)}</>
                        ) : null}
                        {run.source_snapshot.validated_at ? (
                          <> · Inventory validated: {reportingTime(run.source_snapshot.validated_at, run.timezone)}</>
                        ) : null}
                      </p>
                    ) : null}
                  </div>
                </details>
                {admin && run.deliveries?.length ? (
                  <details>
                    <summary className="cursor-pointer font-medium">
                      Email attempts · {run.deliveries.length}
                    </summary>
                    <ol className="mt-3 space-y-3">
                      {run.deliveries.map((delivery, index) => (
                        <li key={`${delivery.attempt_id ?? "legacy"}:${index}`} className="border-l-2 pl-3">
                          <p>{delivery.recipient} · {reportingDeliveryStatus(delivery.status)}</p>
                          {delivery.attempt_id ? <p>Attempt: {delivery.attempt_id}</p> : null}
                          {delivery.updated_at ? <p>Updated: {reportingTime(delivery.updated_at, run.timezone)}</p> : null}
                          {delivery.status === "failed" && delivery.retry_at ? (
                            <p>Retry scheduled: {reportingTime(delivery.retry_at, run.timezone)}</p>
                          ) : null}
                          {delivery.error ? <p>{reportingError(delivery.error)}</p> : null}
                        </li>
                      ))}
                    </ol>
                  </details>
                ) : null}
                {run.error ? (
                  <p role="alert" className="text-destructive">
                    {reportingError(run.error)}
                  </p>
                ) : null}
                {run.download_available && !(run.artifact_expires_at && Date.parse(run.artifact_expires_at) <= Date.now()) ? (
                  <Button aria-label={`Download ${run.format === "xlsx" ? "workbook" : "CSV"} ${run.id}`} disabled={download.isPending} onClick={() => download.mutate(run)}>
                    Download {run.format === "xlsx" ? "workbook" : "CSV"}
                  </Button>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {run.artifact_expires_at && Date.parse(run.artifact_expires_at) <= Date.now()
                      ? `${run.format === "xlsx" ? "Workbook" : "CSV"} expired — download unavailable.`
                      : "Download unavailable (not generated, expired, or failed)."}
                  </p>
                )}
              </article>
            ))}
          </section> : null}
        </>
      ) : null}
    </div>
  );
}

function ExportForm({
  draft,
  capabilities,
  feeders,
  admin,
  editing,
  mode,
  onSaved,
  onCancel,
}: {
  draft?: ReportDraft | null;
  capabilities: ReportingCapabilities;
  feeders: AnalyticsFeeder[];
  admin: boolean;
  editing: ReportingSubscription | null;
  mode: "run" | "subscription";
  onSaved: () => void;
  onCancel: () => void;
}): JSX.Element {
  const { toast } = useToast();
  const { me } = useAuth();
  const schema = reportingSchema(
    capabilities,
    feeders.map((feeder) => feeder.dev_eui),
  );
  type Values = z.input<typeof schema>;
  const [error, setError] = useState("");
  const [outcomeUnknown, setOutcomeUnknown] = useState(false);
  const submissionInFlight = useRef(false);
  const requestStarted = useRef(false);
  const workbookAvailable = capabilities.templates.some((template) => template.id === "executive-report");
  const initialTemplate = editing?.template ?? (workbookAvailable && (!draft || draft.report_view.report === "executive")
    ? "executive-report" : "telemetry-readings");
  const [reportView, setReportView] = useState(editing?.report_view ?? draft?.report_view ??
    (initialTemplate === "executive-report" ? workbookScope(feeders) : null));
  const [contextRemoved, setContextRemoved] = useState(false);
  const initialRange = completedWatPeriod(1);
  const standardColumns = capabilities.columns.filter((column) => STANDARD_READING_COLUMNS.includes(column.id)).map((column) => column.id);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      template: initialTemplate,
      source: editing?.source ?? capabilities.sources[0] ?? "postgres",
      feeder_ids: editing?.feeder_ids ?? draft?.feeder_ids ??
        (reportView ? workbookFeeders(reportView, feeders) : []),
      columns: editing?.columns ?? (initialTemplate === "executive-report" ? [] : standardColumns),
      timezone: editing?.timezone ?? "Africa/Lagos",
      recipients: editing?.recipients.join(", ") ?? "",
      from_time: draft?.from_time ?? initialRange.from_time,
      to_time: draft?.to_time ?? initialRange.to_time,
      name: editing?.name ?? "",
      frequency: editing?.schedule.frequency ?? "daily",
      time: editing?.schedule.time ?? "09:00",
      weekday: editing?.schedule.weekday ?? 0,
      month_day: editing?.schedule.month_day ?? 1,
      period: editing ? subscriptionPeriod(editing) : "previous_day",
      publication_delay_minutes: editing?.publication_delay_minutes ?? 0,
      enabled: editing?.enabled ?? true,
    },
  });
  const values = form.watch();
  const contextValid = !reportView || reportScopeMatches(reportView, values.feeder_ids, feeders);
  const contextBody = () => {
    if (values.template === "executive-report" && reportView?.report !== "executive")
      throw new Error("Choose the workbook's program and feeder scope before continuing.");
    if (!contextValid) throw new Error("Feeder selection changed. Restore the exact report scope or remove report context before continuing.");
    return reportView ? { report_view: reportView } : contextRemoved ? { report_view: null } : {};
  };
  const draftKey = JSON.stringify({ values, reportView });
  const subscriptionBody = (input: Values): ReportingSubscriptionInput => {
    const parsed = schema.parse(input);
    if (!admin || !me?.tenant_id) throw new Error("Administrator organization access required");
    if (!parsed.name.trim()) throw new Error("Subscription name is required");
    const recipients = parseRecipients(parsed.recipients);
    if (!recipients.length) throw new Error("At least one recipient is required for a subscription");
    return {
      ...contextBody(),
      template: parsed.template, source: parsed.source, feeder_ids: parsed.feeder_ids,
      columns: capabilities.columns.filter((column) => parsed.columns.includes(column.id)).map((column) => column.id),
      timezone: parsed.timezone, recipients, name: parsed.name.trim(), enabled: parsed.enabled,
      period: parsed.period, publication_delay_minutes: parsed.publication_delay_minutes,
      schedule: { frequency: parsed.frequency, time: parsed.time,
        weekday: parsed.frequency === "weekly" ? parsed.weekday : null,
        month_day: parsed.frequency === "monthly" ? parsed.month_day : null },
    };
  };
  const preview = useMutation({
    retry: false,
    mutationFn: async (input: Values) => ({
      key: JSON.stringify({ values: input, reportView }),
      result: await reportingApi.previewSubscription(subscriptionBody(input)),
    }),
  });
  const previewResult = preview.data?.key === draftKey ? preview.data.result : null;
  const previewValid = schema.safeParse(values).success && !!values.name.trim() &&
    parseRecipients(values.recipients).length > 0 && !!me?.tenant_id && admin && contextValid;
  const mutation = useMutation({
    retry: false,
    mutationFn: async (input: Values) => {
      const parsed = schema.parse(input);
      const recipients = admin ? parseRecipients(parsed.recipients) : [];
      if (mode === "run" && recipients.length && !capabilities.email_enabled)
        throw new Error("Email delivery is unavailable");
      const definition = {
        ...contextBody(),
        template: parsed.template,
        source: parsed.source,
        feeder_ids: parsed.feeder_ids,
        columns: capabilities.columns
          .filter((column) => parsed.columns.includes(column.id))
          .map((column) => column.id),
        timezone: parsed.timezone,
        recipients,
      };
      if (mode === "run") {
        const rangeError = reportingRangeError(
          parsed.from_time,
          parsed.to_time,
          capabilities.limits.max_range_days,
        );
        if (rangeError) throw new Error(rangeError);
        requestStarted.current = true;
        return reportingApi.createRun({
          ...definition,
          from_time: parsed.from_time,
          to_time: parsed.to_time,
        });
      }
      if (!admin) throw new Error("Administrator access required");
      if (!parsed.name.trim()) throw new Error("Subscription name is required");
      if (!recipients.length)
        throw new Error("At least one recipient is required for a subscription");
      if (parsed.enabled && !capabilities.email_enabled)
        throw new Error("Email delivery is unavailable; save a paused subscription instead");
      const body = subscriptionBody(input);
      requestStarted.current = true;
      return reportingApi.saveSubscription(body, editing?.id);
    },
    onSuccess: () => {
      toast({ title: mode === "run" ? "Export queued" : "Subscription saved", variant: "success" });
      onSaved();
    },
    onError: (cause: Error) => {
      const ambiguous = requestStarted.current &&
        !(cause instanceof ApiError && cause.status >= 400 && cause.status < 500);
      setOutcomeUnknown(ambiguous);
      setError(ambiguous
        ? `Request outcome is unknown. ${mode === "run"
          ? "The export may already be queued. Refresh Run history"
          : "The subscription may already be saved. Refresh subscriptions and check the saved settings"} before submitting again. No automatic retry was made.`
        : reportingError(cause.message));
    },
    onSettled: () => {
      submissionInFlight.current = false;
      requestStarted.current = false;
    },
  });
  const supported = capabilities.templates.some((template) => template.id === values.template);
  return (
    <form
      className={panel}
      aria-label={editing ? "Edit subscription" : "Export configuration"}
      onChange={() => preview.reset()}
      onSubmit={form.handleSubmit((input) => {
        if (submissionInFlight.current || outcomeUnknown) return;
        submissionInFlight.current = true;
        setError("");
        mutation.mutate(input);
      })}
    >
      <h2 className="text-lg font-semibold">
        {editing
          ? `Edit ${editing.name}`
          : mode === "run"
            ? "Create an export"
            : "Create subscription"}
      </h2>
      {draft ? <p role="status" className="text-sm text-muted-foreground">Report draft only. Nothing has been saved, queued, or emailed. Review the scope, period and delivery settings below.</p> : null}
      {reportView && values.template !== "executive-report" ? (
        <section aria-label="Report context" className="space-y-2 rounded-lg border p-3">
          <p>Read-only report context: {reportView.report} · display timezone Africa/Lagos</p>
          {validReportView(reportView) ? <details><summary className="cursor-pointer">Saved report filters</summary><dl>{Object.entries(reportView.filters).filter(([, value]) => value !== "__ALL__").map(([key, value]) =>
            <div key={key}><dt className="inline">{key}: </dt><dd className="inline">{value}</dd></div>)}</dl></details> : null}
          <p>Raw CSV is not a chart/table export. Report links require existing app access and requery current retained data (30 days), which may differ. Monthly periods may exceed dashboard retention. Scheduling timezone does not change the report display timezone.</p>
          <Button type="button" variant="outline" onClick={() => {
            setReportView(null); setContextRemoved(true); preview.reset();
          }}>Remove report context</Button>
          {!contextValid ? <p role="alert">Feeder scope changed. Restore the exact report selection or remove report context before previewing or saving.</p> : null}
        </section>
      ) : null}
      {!supported ? <p role="alert">This report product is unavailable in this deployment.</p> : null}
      <fieldset disabled={mutation.isPending || !supported} className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_18rem]">
        <div className="min-w-0 space-y-5">
        <label className="block">
          Report product
          <select className={selectClass} {...form.register("template", {
            onChange: (event: ChangeEvent<HTMLSelectElement>) => {
              const workbook = event.target.value === "executive-report";
              form.setValue("columns", workbook ? [] : standardColumns);
              if (workbook) {
                const view = reportView?.report === "executive" ? reportView : workbookScope(feeders);
                setReportView(view); setContextRemoved(false);
                form.setValue("feeder_ids", workbookFeeders(view, feeders));
                form.setValue("source", "postgres"); form.setValue("timezone", "Africa/Lagos");
                if (values.period === "previous_month") form.setValue("period", "previous_day");
              }
            },
          })}>
            {!workbookAvailable ? <option value="executive-report" disabled>Executive report workbook · unavailable</option> : null}
            {capabilities.templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.id === "executive-report" ? "Executive report · Excel workbook" : "Supporting readings · CSV"}
              </option>
            ))}
          </select>
        </label>
        {values.template === "executive-report" ? (
          <p role="status" className="rounded-lg bg-muted/40 p-3 text-sm text-muted-foreground">Excel workbook with DAR/service summary, dashboard summary, feeder compliance,
            condition counts and report information on separate sheets. Uses current retained PostgreSQL,
            Africa/Lagos and the exact Executive Summary filter scope; not raw telemetry or a queued-time snapshot.
            Empty values remain unknown. Periods outside 30-day retention cannot be generated.</p>
        ) : <p className="text-sm text-muted-foreground">Underlying measurements in raw source units, with UTC and WAT timestamp options. This is supporting data, not a calculated compliance report.</p>}
        {values.template === "executive-report" ? (
          <div className="space-y-2 rounded-lg border p-4 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={values.columns.length > 0}
                disabled={!["feeder_id", "device_timestamp_at", "device_timestamp_wat"]
                  .every((id) => capabilities.columns.some((column) => column.id === id))}
                onChange={(event) => form.setValue("columns", event.target.checked
                  ? capabilities.columns.filter((column) => [
                    "feeder_id", "device_timestamp_at", "device_timestamp_wat", "ingested_at",
                    "controller_id", "meter_numeric_id", "v_l12_x100", "a_l1_x100", "active_power_inst_x10",
                  ].includes(column.id)).map((column) => column.id) : [])} />
              Include supporting readings in this workbook
            </label>
            <p className="text-xs text-muted-foreground">
              Adds a worksheet of raw readings with UTC and WAT timestamps. Whole-workbook limits
              still apply; large scopes or periods may need a separate readings export.
            </p>
          </div>
        ) : null}
        <section className="space-y-4 rounded-lg border p-4" aria-label="Report scope">
          <h3 className="font-semibold">1. Report scope</h3>
          {values.template === "executive-report" && reportView ? (
            <>
              <ReportScopeFilters report="executive" layout="form" feeders={feeders}
                filters={{ ...reportView.filters, from: "now-1d", to: "now" }}
                onChange={(filters) => {
                  const view = workbookView(filters); setReportView(view);
                  form.setValue("feeder_ids", workbookFeeders(view, feeders), { shouldValidate: true });
                  preview.reset();
                }} />
              <p className="text-sm font-medium">{values.feeder_ids.length} feeders in this report · DAR/service summary by DisCo, plus a total</p>
              {values.feeder_ids.length > 64 ? <p role="alert" className="text-sm text-destructive">Narrow the scope to at most 64 feeders. No partial selection will be exported.</p> : null}
              {!contextValid ? <p role="alert" className="text-sm text-destructive">Choose a program and matching feeder scope before continuing.</p> : null}
            </>
          ) : (
            <ReportingFeederPicker feeders={feeders} selected={values.feeder_ids}
              onChange={(ids) => form.setValue("feeder_ids", ids, { shouldValidate: true })} />
          )}
          {values.template !== "executive-report" ? <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground">Data source & coverage</summary>
            <label className="mt-3 block">
              Source
              <select className={selectClass} {...form.register("source")}>
                {capabilities.sources.map((source) => (
                  <option key={source} value={source}>
                    {source === "postgres"
                      ? "Retained PostgreSQL readings"
                      : "Archived Parquet readings"}
                  </option>
                ))}
              </select>
            </label>
            <p className="text-sm text-muted-foreground">
              Sources are separate, not merged. Archive exports may not include recently ingested
              readings; PostgreSQL exports cover retained history only.
            </p>
          </details> : null}
        </section>
        {values.template !== "executive-report" || values.columns.length > 0 ? <details className="rounded-lg border p-4">
          <summary className="cursor-pointer font-medium">
            Customize columns · {values.columns.length} selected
          </summary>
          <div className="mt-4">
            <fieldset>
              <legend className="mb-2 font-medium">
                CSV columns (raw source units; catalog order)
              </legend>
              <div className="grid gap-2 md:grid-cols-2">
                {capabilities.columns.map((column) => (
                  <label key={column.id} className="flex items-center gap-2">
                    <input type="checkbox" checked={values.columns.includes(column.id)}
                      onChange={(event) => form.setValue("columns", event.target.checked
                        ? [...values.columns, column.id] : values.columns.filter((id) => id !== column.id))}
                      disabled={values.template === "executive-report" &&
                        ["feeder_id", "device_timestamp_at", "device_timestamp_wat"].includes(column.id)} />
                    <span title={column.id}>{column.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>
        </details> : null}
        <details open className="rounded-lg border p-4">
          <summary className="cursor-pointer font-medium">
            2. {mode === "run" ? "Report period" : "Delivery schedule"}
          </summary>
          <div className="mt-4 space-y-4">
            {mode === "run" ? <ReportingPeriodPicker from={values.from_time} to={values.to_time} custom={!!draft}
              onChange={(range) => {
                form.setValue("from_time", range.from_time); form.setValue("to_time", range.to_time);
              }} /> : null}
            <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground">Calendar timezone</summary>
            <label className="block">
              IANA timezone
              <Input list="reporting-timezones" disabled={values.template === "executive-report"} {...form.register("timezone")} />
              <datalist id="reporting-timezones">
                {[
                  "Africa/Lagos",
                  "UTC",
                  "America/New_York",
                  "Europe/London",
                  "Asia/Kolkata",
                  "Australia/Sydney",
                ].map((zone) => (
                  <option key={zone} value={zone} />
                ))}
              </datalist>
            </label>
            </details>
            {mode === "run" ? (
              <details className="text-sm">
              <summary className="cursor-pointer text-muted-foreground">Exact timestamps (advanced)</summary>
              <div className="grid gap-3 md:grid-cols-2">
                <label>
                  From UTC ISO (inclusive)
                  <Input {...form.register("from_time")} placeholder="2026-10-01T00:00:00Z" />
                  <span className="text-sm text-muted-foreground">
                    {reportingTime(values.from_time, values.timezone)}
                  </span>
                </label>
                <label>
                  To UTC ISO (exclusive)
                  <Input {...form.register("to_time")} placeholder="2026-10-02T00:00:00Z" />
                  <span className="text-sm text-muted-foreground">
                    {reportingTime(values.to_time, values.timezone)}
                  </span>
                </label>
                <p className="text-sm md:col-span-2">
                  Enter explicit absolute instants ending in Z. Timezone changes labels, not these
                  instants. Interval is [from, to).
                </p>
              </div>
              </details>
            ) : (
              <>
                <label className="block">
                  Subscription name
                  <Input {...form.register("name")} />
                </label>
                <label className="block">
                  Frequency
                  <select className={selectClass} {...form.register("frequency")}>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </label>
                <label className="block">
                  Scheduled local time (HH:MM)
                  <Input type="time" {...form.register("time")} />
                </label>
                {values.frequency === "weekly" ? (
                  <label className="block">
                    Day of week
                    <select className={selectClass} {...form.register("weekday")}>
                      {[
                        "Monday",
                        "Tuesday",
                        "Wednesday",
                        "Thursday",
                        "Friday",
                        "Saturday",
                        "Sunday",
                      ].map((day, index) => (
                        <option key={day} value={index}>
                          {day}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                {values.frequency === "monthly" ? (
                  <label className="block">
                    Day of month
                    <Input type="number" min={1} max={31} {...form.register("month_day")} />
                    <span>Days 29–31 clamp to the last day in shorter months.</span>
                  </label>
                ) : null}
                <label className="block">
                  Completed dataset period
                  <select className={selectClass} {...form.register("period")}>
                    <option value="previous_day">Previous completed day</option>
                    <option value="previous_week">Last 7 completed local days</option>
                    <option value="previous_month" disabled={values.template === "executive-report"}>
                      Previous complete calendar month
                    </option>
                  </select>
                </label>
                <details className="text-sm">
                <summary className="cursor-pointer text-muted-foreground">Advanced timing</summary>
                <label className="mt-3 block">
                  Publication delay (minutes)
                  <Input type="number" min={0} max={1440} {...form.register("publication_delay_minutes")} />
                </label>
                <p>
                  Exports the {reportingPeriodDescription(values.period ?? "previous_day")}. Frequency and dataset period are independent.
                  Delay postpones sending after the nominal time without changing the nominal-date dataset period.{" "}
                  Calendar days follow the chosen timezone, including daylight-saving changes. The
                  server resolves schedule occurrences.
                </p>
                </details>
                <label className="flex items-center gap-2">
                  <input type="checkbox" {...form.register("enabled")} />
                  Enabled (uncheck to save paused)
                </label>
              </>
            )}
          </div>
        </details>
        {admin ? (
          <details open={mode === "subscription"} className="rounded-lg border p-4">
            <summary className="cursor-pointer font-medium">
              {mode === "run" ? "Optional email delivery" : "Delivery recipients"}
            </summary>
            <div className="mt-4 space-y-3">
              <label className="block">
                Recipients (comma-separated email addresses; no default)
                <Input
                  {...form.register("recipients")}
                  disabled={!capabilities.email_enabled && mode === "run"}
                />
                <span className="text-sm text-muted-foreground">
                  Fixed sender: Anietronic.{" "}
                  {mode === "run"
                    ? "Leave empty for download only."
                    : "At least one recipient required."}
                </span>
              </label>
              {me?.email ? <Button type="button" variant="outline" size="sm"
                disabled={!capabilities.email_enabled && mode === "run"}
                onClick={() => form.setValue("recipients", me.email)}>Use my account email</Button> : null}
              <p className="text-sm text-muted-foreground">
                External email attachments are an administrator-approved disclosure and cannot be
                revoked after sending. Download links require a provisioned user with current
                organization access; adding a recipient does not create an account. Subscriptions
                remain tied to their creator's authorization; revoked administrator access stops
                delivery.
              </p>
            </div>
          </details>
        ) : null}
        </div>
        <aside className="space-y-4 rounded-lg border bg-muted/20 p-4 xl:sticky xl:top-6" aria-label="Review report">
          <h3 className="font-semibold">{mode === "run" ? "Ready to export?" : "Review delivery"}</h3>
          <dl className="space-y-3 text-sm">
            <div><dt className="text-xs text-muted-foreground">Report</dt><dd className="font-medium">{values.template === "executive-report" ? "Executive report · Excel" : "Supporting readings · CSV"}</dd></div>
            <div><dt className="text-xs text-muted-foreground">Scope</dt><dd>{values.feeder_ids.length} feeders{values.template !== "executive-report" ? ` · ${values.columns.length} columns` : ""}</dd></div>
            <div><dt className="text-xs text-muted-foreground">Period</dt><dd className="break-words">{mode === "run"
              ? `${reportingTime(values.from_time, values.timezone).split(" · ")[0]} → ${reportingTime(values.to_time, values.timezone).split(" · ")[0]}`
              : reportingPeriodDescription(values.period ?? "previous_day")}</dd></div>
            <div><dt className="text-xs text-muted-foreground">Delivery</dt><dd>{parseRecipients(values.recipients).length
              ? `${parseRecipients(values.recipients).length} email recipient(s)` : "Download only"}</dd></div>
            {mode === "subscription" ? <div><dt className="text-xs text-muted-foreground">Schedule</dt><dd>{values.frequency} at {values.time} · {values.enabled ? "Active on save" : "Save paused"}</dd></div> : null}
          </dl>
        <p className="text-xs text-muted-foreground">
          Maximum {capabilities.limits.max_rows.toLocaleString()} rows ·{" "}
          {capabilities.limits.max_bytes.toLocaleString()} bytes ·{" "}
          {capabilities.limits.max_range_days} days. Narrow your selection if a limit is exceeded;
          partial exports are not complete results. Requests are queued, not immediate.
        </p>
        {Object.entries(form.formState.errors).map(([key, issue]) => (
          <p role="alert" key={key} className="text-destructive">
            {key.replace(/_/g, " ")}: {issue.message ?? "Invalid selection"}
          </p>
        ))}
        {error ? (
          <p role="alert" className="text-destructive">
            {error}
          </p>
        ) : null}
        {outcomeUnknown ? (
          <Button type="button" variant="outline" onClick={() => {
            setOutcomeUnknown(false);
            setError("");
          }}>I checked history and subscriptions; allow another request</Button>
        ) : null}
        <div className="flex flex-col gap-2">
          {mode === "subscription" ? (
            <Button type="button" variant="outline" disabled={!previewValid || preview.isPending}
              onClick={() => preview.mutate(form.getValues())}>
              {preview.isPending ? "Previewing schedule…" : "Preview schedule"}
            </Button>
          ) : null}
          <Button type="submit" disabled={outcomeUnknown}>
            {mutation.isPending
              ? "Saving…"
              : mode === "run"
                ? values.template === "executive-report" ? "Queue report workbook" : "Queue raw telemetry CSV"
                : "Save subscription"}
          </Button>
          {mode === "subscription" ? (
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel editing
            </Button>
          ) : null}
        </div>
        {preview.error ? <p role="alert">{reportingError(preview.error.message)}</p> : null}
        {previewResult ? (
          <section aria-label="Schedule preview" className="space-y-2 rounded-lg border p-4">
            <h3 className="font-semibold">Unsaved draft schedule preview</h3>
            <p>Planning only: not a data preview. Nothing is stored, exported or emailed. This is not the saved subscription or a self-only test.</p>
            <p>Nominal next time: {reportingTime(previewResult.scheduled_for, previewResult.timezone)}</p>
            <p>Actual delayed next time: {reportingTime(previewResult.next_run_at, previewResult.timezone)}</p>
            <p>Dataset: {reportingPeriodDescription(previewResult.period)} · delay {previewResult.publication_delay_minutes} minutes</p>
            <p>Next scheduled period [inclusive, exclusive): {reportingTime(previewResult.from_time, previewResult.timezone)} → {reportingTime(previewResult.to_time, previewResult.timezone)}</p>
            <p>Last completed period at request time (self-test timing, delay ignored): {reportingTime(previewResult.test_from_time, previewResult.timezone)} → {reportingTime(previewResult.test_to_time, previewResult.timezone)}</p>
          </section>
        ) : null}
        </aside>
      </fieldset>
    </form>
  );
}
